import {
  makeStyles,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box
} from '@material-ui/core';
import { useState, useRef, useEffect } from 'react';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import { SSearchSelectDropdownProps } from '..';
import { SEmptyState } from '../../../../..';
import { Loader } from '../loader';

const useStyles = makeStyles({
  iconEnd: {
    marginLeft: 'auto',
    minWidth: 'auto'
  }
});

type ContentProps<T> = Pick<
  SSearchSelectDropdownProps<T>,
  'list' | 'loading' | 'search' | 'isSelected' | 'onSelect' | 'renderRow'
>;

export const DropdownContent = <T,>({
  list,
  loading,
  search,
  isSelected,
  onSelect,
  renderRow
}: ContentProps<T>) => {
  const classes = useStyles();
  const [_focusedIndex, setFocusedIndex] = useState<number>(0);
  const listItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const listRef = useRef<HTMLUListElement>(null);

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    let nextIndex: number;
    let prevIndex: number;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = index + 1 < list.length ? index + 1 : 0;
        setFocusedIndex(nextIndex);
        setTimeout(() => {
          listItemRefs.current[nextIndex]?.focus();
        }, 0);
        break;
      case 'ArrowUp':
        event.preventDefault();
        prevIndex = index - 1 >= 0 ? index - 1 : list.length - 1;
        setFocusedIndex(prevIndex);
        setTimeout(() => {
          listItemRefs.current[prevIndex]?.focus();
        }, 0);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleArrowDownFocus = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        listItemRefs.current[0]?.focus();
      }
    };

    document.addEventListener('keydown', handleArrowDownFocus);

    return () => {
      document.removeEventListener('keydown', handleArrowDownFocus);
    };
  }, []);

  if (loading) return <Loader />;

  if (list?.length === 0)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
        <SEmptyState
          subTitle={
            search.length > 0
              ? 'No results found for your search. Please try again with a different search term'
              : 'No results found'
          }
        />
      </Box>
    );

  return (
    <List
      ref={listRef}
      disablePadding
      onKeyDown={e => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          listItemRefs.current[0]?.focus();
        }
      }}>
      {list?.map((item, index) => (
        <ListItem
          ref={el => {
            listItemRefs.current[index] = el;
          }}
          style={{
            padding: '2px 8px'
          }}
          button
          onClick={() => onSelect?.(item)}
          onKeyDown={e => handleKeyDown(e, index)}
          tabIndex={0}>
          <ListItemText
            primary={renderRow(item)}
            style={{
              color: isSelected?.(item) ? '#2196F3' : 'inherit'
            }}
          />
          <ListItemIcon className={classes.iconEnd}>
            <CheckRoundedIcon
              fontSize="small"
              htmlColor={isSelected?.(item) ? '#2196F3' : 'transparent'}
            />
          </ListItemIcon>
        </ListItem>
      ))}
    </List>
  );
};
